<template>
  <CustomNuxtLink
    :to="url"
    :class="['group', { 'domain-link-menu': menu, 'domain-link-inverted': inverted }]"
    :data-domain="domain">
    <span class="block relative w-fit">
      <svg
        v-if="domain === 'youth-support'"
        :class="[svgClass, 'w-[140%] -ml-[25%]']"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 100 25">
        <path
          d="M0 15.28c0 3.17.88 5.582 2.64 7.238C4.397 24.173 6.77 25 9.757 25h81.457c2.778 0 4.936-.762 6.476-2.29C99.23 21.185 100 18.94 100 15.98V0H10.869v15.28H0Z"
          :fill="fillColor" />
      </svg>
      <!-- Inverted variant: second layer for hover animation -->
      <svg
        v-if="domain === 'youth-support' && inverted"
        :class="[invertedHoverSvgClass, 'w-[140%] -ml-[25%]']"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 100 25">
        <path
          d="M0 15.28c0 3.17.88 5.582 2.64 7.238C4.397 24.173 6.77 25 9.757 25h81.457c2.778 0 4.936-.762 6.476-2.29C99.23 21.185 100 18.94 100 15.98V0H10.869v15.28H0Z"
          :fill="domainFills[domain]" />
      </svg>
      <svg
        v-if="domain === 'community-work'"
        :class="[svgClass, 'w-[128%] -ml-[10%]']"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 94 25">
        <path
          d="M84.912 25c2.713 0 4.795-.594 6.245-1.783 1.451-1.189 2.176-2.878 2.176-5.067 0-1.436-.418-2.701-1.256-3.796-.837-1.095-2.01-1.819-3.52-2.172 1.273-.33 2.294-.994 3.06-1.995.767-1 1.15-2.23 1.15-3.69 0-2.119-.73-3.731-2.193-4.837C89.11.553 87.545 0 84.998 0H0v25h84.912Z"
          :fill="fillColor" />
      </svg>
      <svg
        v-if="domain === 'community-work' && inverted"
        :class="[invertedHoverSvgClass, 'w-[128%] -ml-[10%]']"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 94 25">
        <path
          d="M84.912 25c2.713 0 4.795-.594 6.245-1.783 1.451-1.189 2.176-2.878 2.176-5.067 0-1.436-.418-2.701-1.256-3.796-.837-1.095-2.01-1.819-3.52-2.172 1.273-.33 2.294-.994 3.06-1.995.767-1 1.15-2.23 1.15-3.69 0-2.119-.73-3.731-2.193-4.837C89.11.553 87.545 0 84.998 0H0v25h84.912Z"
          :fill="domainFills[domain]" />
      </svg>
      <svg
        v-if="domain === 'child-care'"
        :class="[svgClass, 'w-[125%] -ml-[7%]']"
        viewBox="0 0 127 25"
        fill="none"
        xmlns="http://www.w3.org/2000/svg">
        <path d="M126.959 25L117.709 12.1459L126.959 0H0V25H126.959Z" :fill="fillColor" />
      </svg>
      <svg
        v-if="domain === 'child-care' && inverted"
        :class="[invertedHoverSvgClass, 'w-[125%] -ml-[7%]']"
        viewBox="0 0 127 25"
        fill="none"
        xmlns="http://www.w3.org/2000/svg">
        <path d="M126.959 25L117.709 12.1459L126.959 0H0V25H126.959Z" :fill="domainFills[domain]" />
      </svg>
      <svg
        v-if="domain === 'maternity-care'"
        :class="[svgClass, 'w-[138%] -ml-[10%]']"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 111 25">
        <path d="M0 0v25h110.769l-7.325-9.459 7.155-9.595h-13.53V0H0Z" :fill="fillColor" />
      </svg>
      <svg
        v-if="domain === 'maternity-care' && inverted"
        :class="[invertedHoverSvgClass, 'w-[138%] -ml-[10%]']"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 111 25">
        <path d="M0 0v25h110.769l-7.325-9.459 7.155-9.595h-13.53V0H0Z" :fill="domainFills[domain]" />
      </svg>
      <svg
        v-if="domain === 'head-office'"
        :class="[svgClass, 'w-[125%] -ml-[13%]']"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 124 25">
        <rect
          width="124"
          height="25"
          rx="12.5"
          :fill="fillColor" />
      </svg>
      <svg
        v-if="domain === 'head-office' && inverted"
        :class="[invertedHoverSvgClass, 'w-[125%] -ml-[13%]']"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 124 25">
        <rect
          width="124"
          height="25"
          rx="12.5"
          :fill="domainFills[domain]" />
      </svg>

      <span class="block relative w-fit h-fit">
        <Typography as="span" :variant="variant" class="block relative font-bold tracking-tight z-[1]">
          {{ label }}
        </Typography>
        <Typography
          as="span"
          :variant="variant"
          :class="[
            'absolute inset-0 font-bold tracking-tight z-[1] transition-[clip-path] duration-300 ease-smooth',
            active ? '[clip-path:inset(0_0_0_0)]' : '[clip-path:inset(0_100%_0_0)]',
            'group-hover:[clip-path:inset(0_0_0_0)]',
            domain === 'head-office' ? 'text-primary-100' : 'text-white',
          ]"
          aria-hidden>
          {{ label }}
        </Typography>
      </span>
    </span>
  </CustomNuxtLink>
</template>

<script setup lang="ts">
import { domainLabels, type Domain } from '@gro-up/types'
import { CustomNuxtLink, Typography } from '@gro-up/ui/primitives'
import { computed } from 'vue'

import type { TypographyVariant } from '../Typography/Typography.vue'

export type DomainLinkProps = {
  domain: Domain
  url: string
  variant?: TypographyVariant
  inverted?: boolean
  menu?: boolean
  active?: boolean
}

const props = withDefaults(defineProps<DomainLinkProps>(), {
  variant: 'caption',
  inverted: false,
  menu: false,
  active: false,
})

const label = computed(() => domainLabels[props.domain])

const baseSvgClass =
  'absolute top-1/2 -translate-y-1/2 left-0 [clip-path:inset(0_100%_0_0)] group-hover:[clip-path:inset(0_0_0_0)] transition-[clip-path] duration-300 ease-smooth'

const invertedBaseSvgClass =
  'absolute top-1/2 -translate-y-1/2 left-0 [clip-path:inset(0_0_0_0)] transition-fill duration-300 ease-smooth'

const invertedHoverSvgClass = computed(() => {
  const baseClass = 'absolute top-1/2 -translate-y-1/2 left-0 transition-[clip-path] duration-300 ease-smooth'
  const activeClass = props.active ? '[clip-path:inset(0_0_0_0)]' : '[clip-path:inset(0_100%_0_0)]'
  return `${baseClass} ${activeClass} group-hover:[clip-path:inset(0_0_0_0)]`
})

const menuSvgClass =
  'absolute top-1/2 -translate-y-1/2 left-0 [clip-path:inset(0_0_0_0)] group-hover:[clip-path:inset(0_0_0_0)] transition-[clip-path] duration-300 ease-smooth'

const svgClass = computed(() => {
  if (props.menu) {
    return menuSvgClass
  }
  return props.inverted ? invertedBaseSvgClass : baseSvgClass
})

const domainFills: Record<Domain, string> = {
  'youth-support': '#2A8D9A',
  'community-work': 'red',
  'child-care': '#E89900',
  'maternity-care': '#622E60',
  'head-office': '#E1DCD0',
}

const fillColor = computed(() => {
  if (props.inverted) {
    return '#1F122E'
  }
  return domainFills[props.domain]
})
</script>
